/* eslint-disable react/no-array-index-key */
import { useState, useRef, useEffect, ComponentRef } from 'react';
import { Modal, ModalContent, ModalBody } from '@ds';
import ReactPlayer from 'react-player/file';
import { ClinicalMediaType } from '@/api/apis/case';
import { useMediaImageLoader } from './hooks';
import { MediaPreviewContent } from './MediaPreviewContent';
import { MediaPreviewFooter } from './MediaPreviewFooter';
import { MediaPreviewHeader } from './MediaPreviewHeader';
import { MediaPreviewThumbnails } from './MediaPreviewThumbnails';

interface MediaPreviewProps {
  heading?: string;
  media: ClinicalMediaType[];
  onClose: () => void;
  open: boolean;
  selectedMediaId: string;
  isFromClinicalLog?: boolean;
  onShareClick?: () => void;
  useDirectPath?: boolean;
}

export const MediaPreview = ({
  heading,
  media,
  onClose,
  open,
  selectedMediaId,
  isFromClinicalLog = false,
  onShareClick,
  useDirectPath = false,
}: MediaPreviewProps) => {
  const getInitialIndex = () => {
    if (!media?.length) return 0;

    const index = media.findIndex((item) => item?.file?.fileId === selectedMediaId);
    return index === -1 ? 0 : index;
  };

  const [currentIndex, setCurrentIndex] = useState(getInitialIndex);
  const [scale, setScale] = useState(1);

  const playerRef = useRef<ComponentRef<typeof ReactPlayer>>(null);
  const currentMedia = media[currentIndex];

  const { isImageLoading, loadedImageSrc } = useMediaImageLoader({
    currentMedia,
    open,
    useDirectPath,
  });

  useEffect(() => {
    const index = media.findIndex((item) => item?.file?.fileId === selectedMediaId);
    setCurrentIndex(index === -1 ? 0 : index);
    setScale(1);
  }, [selectedMediaId, media]);

  if (!currentMedia?.file) {
    return null;
  }

  const resetScale = () => {
    setScale(1);
  };

  const handleNext = () => {
    if (currentIndex < media.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      resetScale();
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
      resetScale();
    }
  };

  const increaseScale = () => {
    setScale((prevScale) => Math.min(prevScale + 0.1, 3));
  };

  const decreaseScale = () => {
    setScale((prevScale) => Math.max(prevScale - 0.1, 1));
  };

  const handleSelectThumbnail = (index: number) => {
    setCurrentIndex(index);
    resetScale();
  };

  return (
    <Modal hideCloseButton size="full" isOpen={open} onClose={onClose}>
      <ModalContent className="relative flex h-full flex-1 bg-black bg-opacity-60">
        <MediaPreviewHeader
          heading={heading || currentMedia.file.fileName}
          currentMedia={currentMedia}
          onClose={onClose}
          onZoomIn={increaseScale}
          onZoomOut={decreaseScale}
          isFromClinicalLog={isFromClinicalLog}
          onShareClick={onShareClick}
          useDirectPath={useDirectPath}
        />

        <ModalBody className="flex h-full min-h-0 flex-1 flex-col items-center justify-center">
          <MediaPreviewContent
            currentMedia={currentMedia}
            currentIndex={currentIndex}
            mediaLength={media.length}
            scale={scale}
            isImageLoading={isImageLoading}
            loadedImageSrc={loadedImageSrc}
            onPrev={handlePrev}
            onNext={handleNext}
            playerRef={playerRef}
            useDirectPath={useDirectPath}
          />

          {media.length > 1 && (
            <MediaPreviewThumbnails
              media={media}
              currentIndex={currentIndex}
              onSelect={handleSelectThumbnail}
              useDirectPath={useDirectPath}
            />
          )}
        </ModalBody>

        <MediaPreviewFooter currentMedia={currentMedia} />
      </ModalContent>
    </Modal>
  );
};
